<template>
  <div :id="id" class="faq" v-if="$store.getters.isFaqLoaded">
    <div class="componata-container">
      <FancyHeading class="faq__heading">
        <template v-slot:title><span v-html="content.title"></span></template>
      </FancyHeading>

      <Tabs
        :data="navigationTabs"
        @change="showTabContent($event)"
        class="faq__tabs"
      />

      <div class="faq__content-area">
        <section
          v-for="(category, categoryIndex) in $store.getters.getFaq"
          :key="categoryIndex"
          class="faq__content-section"
          :class="{ '--is-visible' : activeCategoryIndex === categoryIndex }"
        >
          <ul class="faq__list">
            <li
              v-for="(question, questionIndex) in category.category_questions"
              :key="questionIndex"
              class="faq__item"
              :class="{'--is-open' : question.isVisible}"
            >
              <h4
                @click="toggleQuestion(categoryIndex, questionIndex)"
                class="faq__question"
              >
                {{ question.question }}
              </h4>
              <div class="faq__answer" v-html="question.answer"></div>
            </li>
          </ul>
        </section>
      </div>

      <div class="faq__footer">
        <h3 class="faq__footer-title">Haben Sie weitere Fragen?</h3>

        <p class="faq__footer-line"><a href="mailto:kundenservice@componata.de" v-on:click="trackMail"><i class="icon icon-envelope"></i> kundenservice@componata.de</a> <span>oder</span> <a href="tel:034198992080" v-on:click="trackPhone"><i class="icon icon-phone"></i> 0341 9899 2080</a></p>
      </div>
    </div>
  </div>
</template>

<script>
import FancyHeading from '@/components/Pages/FancyHeading';
import Tabs from '@/components/Pages/Tabs';

export default {
  name: 'Faq',
  components: {
    FancyHeading,
    Tabs
  },
  props: {
    id: {
      type: String,
      default: 'faq'
    },
    content: {
      type: Object
    }
  },
  data () {
    return {
      navigationTabs: [],
      activeCategoryIndex: 0
    };
  },
  methods: {
    init () {
      this.$store.getters.getFaq.forEach(item => {
        this.navigationTabs.push({
          icon: item.category_icon,
          title: item.category_name,
        });
      });
    },

    showTabContent ($event) {
      this.activeCategoryIndex = $event;
    },

    toggleQuestion (categoryIndex, questionIndex) {
      if (this.$store.getters.getFaq[categoryIndex].category_questions[questionIndex].isVisible) {
        this.$store.getters.getFaq[categoryIndex].category_questions[questionIndex].isVisible = false;
      } else {
        if ('isVisible' in this.$store.getters.getFaq[categoryIndex].category_questions[questionIndex] === false) {
          this.$set(this.$store.getters.getFaq[categoryIndex].category_questions[questionIndex], 'isVisible', true);
        }

        this.$store.getters.getFaq[categoryIndex].category_questions[questionIndex].isVisible = true;
      }
    },
    trackMail() {
      this.trackEvent('mailContactClicked');
    },
    trackPhone() {
      this.trackEvent('phoneContactClicked');
    },
    trackEvent(event) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: event,
        campaing: this.landingPageName
      });
    }
  },
  created () {
    this.$root.$on('faqLoaded', () => {
      this.init();
    });
  },
  updated () {
    this.$nextTick(() => {
      this.$root.$emit('AOSRefresh');
    });
  }
};
</script>

<style lang="scss" scoped>
/**
 * FAQ
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc Frequently Asked Questions
 * @since 2022-07-06
**/

$component: 'faq';

.#{$component} {
  @include padding((map-get($spacers, 6) * 2) 0 (map-get($spacers, 6) * 2) 0);
  background: linear-gradient(#222, #212b36);

  &__heading {
    --from: #6AC2FF;
    --to: #ffffff;
    --shadow: #00000001;

    @include margin(0 auto (map-get($spacers, 5) * 2) auto);
    text-align: center;
  }

  &__tabs {
    --color-active: #6AC2FF;
    @include margin-bottom(map-get($spacers, 6));
    border-bottom-color: rgba($white, 0.1);

    ::v-deep .tabs {
      &__item {
        @include hover () {
          color: $sky-dark;
        }

        &:active {
          color: $ink-lightest;
        }
      }
    }
  }

  &__content-area {
    color: $sky-lighter;
  }

  &__content-section {
    display: none;

    &.--is-visible {
      display: block;
    }
  }

  &__list {
    @include unlist;
    width: 72%;
    margin-left: auto;
    margin-right: auto;
  }

  &__item {
    position: relative;
    border-bottom: 1px solid rgba($ink-light, 50%);

    &.--is-open {
      .#{$component} {
        &__question {
          &:after {
            transform: rotate(44deg);
          }
        }

        &__answer {
          max-height: 500px;
          opacity: 1;
          transition: all 0.3s ease;
        }
      }
    }
  }

  &__question {
    margin: 0;
    @include padding-top(map-get($spacers, 4));
    @include padding-right(map-get($spacers, 6));
    @include padding-bottom(map-get($spacers, 4));
    @include font-size(map-get($lp, 'font-size-lg'));
    font-weight: $font-weight-normal;
    cursor: pointer;

    &:after {
      position: absolute;
      @include rfs(map-get($spacers, 3) * 1.3, top);
      right: 0;
      display: inline-block;
      content: '\e91c';
      @include icomoon;
      @include font-size(map-get($lp, 'font-size-lg') * 1.5);
      transition: transform 0.2s ease-in-out;
    }

    &:hover {
      color: #6AC2FF;
    }
  }

  &__answer {
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    @include padding-right(map-get($spacers, 6));
    color: $sky-dark;
    // font-weight: $font-weight-light;
    line-height: 160%;

    > p:last-child {
      margin-bottom: 0;
    }

    // p + p {
    //   @include margin-top(map-get($spacers, 2));
    // }
  }

  &__footer {
    @include margin-top(map-get($spacers, 6));
    color: $sky-lighter;
    text-align: center;

    a {
      color: $white;
      text-decoration: none;

      &:hover {
        color: #6AC2FF;
      }
    }
  }

  &__footer-title {
    @include margin-bottom(map-get($spacers, 4));
    @include font-size(map-get($lp, 'font-size-lg'));
  }

  &__footer-line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    margin: 0;

    a {
      display: flex;
      align-items: center;
    }
    span {
      display: inline-block;
      @include margin-left(map-get($spacers, 2));
      @include margin-right(map-get($spacers, 2));
      color: $ink-lightest;
    }

    .icon {
      @include margin-right(map-get($spacers, 1));
      color: #6AC2FF;
      @include font-size(1.8rem);
    }
  }

  /** RESPONSIVENESS **/

  // ——————————————————————————————
  // Max LG
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-lg')) {
  }

  // ——————————————————————————————
  // Max MD
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-md')) {
    &__heading {
      width: 70%;
      @include margin(map-get($spacers, 6) * 2 auto map-get($spacers, 4) auto);
      text-align: center;
    }
  }

  // ——————————————————————————————
  // Max SM
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-sm')) {
    &__heading {
      width: 80%;
    }

    &__list {
      width: 90%;
    }
  }

  // ——————————————————————————————
  // Max XS
  // ——————————————————————————————
  @media (max-width: map-get($lp, 'viewport-xs')) {
    &__heading {
      width: 90%;
    }
  }
}
</style>
